import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Medication } from '../../models/medication';
import * as fromMedication from '../reducer/medications.reducer';

const selectMedicationFeatureUnsafe = createFeatureSelector<fromMedication.MedicationState | undefined>(
  fromMedication.medicationFeatureKey
);

export const selectMedicationFeature = createSelector(
  selectMedicationFeatureUnsafe,
  state => state ?? fromMedication.initialState
);

const { selectEntities, selectAll } = fromMedication.medicationAdapter.getSelectors();

export const selectMedicationEntities = createSelector(selectMedicationFeature, selectEntities);

export const selectMedications = createSelector(selectMedicationFeature, selectAll);

export const selectPatientMedicationCount = createSelector(selectMedications, results => results.length);

export const selectError = createSelector(selectMedicationFeature, state => state.baseState.error);

/**
 * The single-medication detail read's failure, deliberately separate from `selectError` so it cannot
 * make the medications list claim it failed. See MedicationState.detailError.
 */
export const selectDetailError = createSelector(selectMedicationFeature, state => state.detailError ?? null);

export const selectIsLoading = createSelector(selectMedicationFeature, state => state.baseState.isLoading);

export const selectSelectedMedications = createSelector(selectMedicationFeature, state => state.selectedMedications);

export const selectMedicationsByNameAsc = createSelector(selectMedications, (meds): Medication[] => {
  return [...meds].sort((a, b) =>
    (a.medicationName ?? '').localeCompare(b.medicationName ?? '', undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  );
});

export const selectSelectedMedicationsData = createSelector(
  selectMedicationsByNameAsc,
  selectSelectedMedications,
  (entities, selectedMeds) => {
    return entities.filter(med =>
      selectedMeds?.some(selectedMed => selectedMed.medicationId === med.patientMedicationId)
    );
  }
);

export const selectMedicationById = (medicationId: number) =>
  createSelector(selectMedicationsByNameAsc, entities => {
    return entities.find(med => med.patientMedicationId === medicationId) ?? null;
  });
